import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Pill } from '@/components/ui/pill';
import { Progress, ProgressFilledTrack } from '@/components/ui/progress';
import { ScrollView } from '@/components/ui/scroll-view';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { SurveyResultQuestion, SurveyResultsData } from '../model/surveyResultsData';
import {
  formatAnsweredCount,
  formatRespondentCount,
  formatVoteCount,
  getOptionPercentage,
  isMatrixCellAnswered,
  isOptionAnswered,
  UNLISTED_OPTION_VALUE,
} from '../model/surveyResults';

type OptionQuestion = Extract<SurveyResultQuestion, { options: unknown }>;

type MatrixQuestion = Extract<SurveyResultQuestion, { kind: 'matrix' }>;

type MatrixRow = MatrixQuestion['rows'][number];

function getMatrixCount(row: MatrixRow, columnValue: MatrixRow['value']): number {
  return row.counts.find((entry) => entry.value === columnValue)?.count ?? 0;
}

/**
 * The percentage and vote count carry the result as text; the bar only
 * restates it, so it is hidden from screen readers rather than announced as a
 * second, unlabelled progress bar. The respondent's own pick is named in words
 * as well, so it never rests on the fill alone.
 *
 * The bar sets its own track and fill because the `Progress` defaults nearly
 * vanish in dark mode: `primary-500` on `background-300` reads 1.04:1 there.
 * `primary-700` on `background-200` keeps the fill distinct from its track and
 * the page in both themes.
 */
function SurveyResultOptions({ question, answer }: { question: OptionQuestion; answer: unknown }) {
  const listedValues = question.options
    .map((option) => option.value)
    .filter((value) => value !== UNLISTED_OPTION_VALUE);

  return (
    <VStack space="md">
      {question.kind === 'multi' ? (
        <Text size="sm" className="text-typography-600">
          People could pick more than one answer, so percentages can add up to more than 100%.
        </Text>
      ) : null}

      {question.options.map((option) => {
        const percentage = getOptionPercentage(option.count, question.answeredCount);

        const isAnswered = isOptionAnswered(answer, option.value, listedValues);

        return (
          <VStack key={String(option.value)} space="xs">
            <HStack className="items-center justify-between gap-3">
              <HStack className="min-w-0 flex-1 flex-wrap items-center gap-2">
                <Text size="sm" className="shrink text-typography-900">
                  {option.label}
                </Text>

                {isAnswered ? <Pill>Your answer</Pill> : null}
              </HStack>

              <Text size="sm" className="shrink-0 text-typography-600">
                {`${percentage}% · ${formatVoteCount(option.count)}`}
              </Text>
            </HStack>

            <HStack aria-hidden>
              <Progress value={percentage} size="sm" className="flex-1 bg-background-200">
                <ProgressFilledTrack className="bg-primary-700" />
              </Progress>
            </HStack>
          </VStack>
        );
      })}
    </VStack>
  );
}

function SurveyResultMatrix({ question, answer }: { question: MatrixQuestion; answer: unknown }) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <VStack>
        <HStack className="pb-2">
          <VStack className="w-40" />

          {question.columns.map((column) => (
            <Text
              key={String(column.value)}
              size="sm"
              className="w-24 px-1 text-center font-semibold text-typography-700">
              {column.label}
            </Text>
          ))}
        </HStack>

        {question.rows.map((row) => (
          <HStack key={String(row.value)} className="items-center border-t border-outline-100 py-2">
            <VStack className="w-40 pr-2">
              <Text size="sm" className="text-typography-900">
                {row.label}
              </Text>

              <Text size="xs" className="text-typography-600">
                {formatAnsweredCount(row.answeredCount)}
              </Text>
            </VStack>

            {question.columns.map((column) => (
              <VStack key={String(column.value)} className="w-24 items-center px-1">
                <Text size="sm" className="text-center text-typography-900">
                  {getMatrixCount(row, column.value)}
                </Text>

                {isMatrixCellAnswered(answer, row.value, column.value) ? (
                  <Text size="xs" className="font-semibold text-typography-700">
                    Yours
                  </Text>
                ) : null}
              </VStack>
            ))}
          </HStack>
        ))}
      </VStack>
    </ScrollView>
  );
}

function SurveyResultQuestionBlock({
  question,
  answer,
}: {
  question: SurveyResultQuestion;
  answer: unknown;
}) {
  const renderBody = () => {
    if (question.kind === 'matrix') {
      return <SurveyResultMatrix question={question} answer={answer} />;
    }

    if (question.kind === 'text' || question.kind === 'unsupported') {
      return null;
    }

    return <SurveyResultOptions question={question} answer={answer} />;
  };

  const answeredCount = formatAnsweredCount(question.answeredCount);

  return (
    <VStack space="sm">
      <Text className="font-semibold text-typography-900">{question.title}</Text>

      {renderBody()}

      <Text size="sm" className="text-typography-600">
        {question.kind === 'text'
          ? `${answeredCount} · written answers stay private`
          : answeredCount}
      </Text>
    </VStack>
  );
}

/**
 * How everyone else answered a poll, shown to a respondent once they have
 * answered it themselves.
 *
 * Each question renders by the kind the API aggregated it as. Choice-like kinds
 * get a bar per option, a matrix gets a table of counts, and free text or
 * question types the API cannot break down show only how many answered: the API
 * never sends other respondents' written answers, and the text question says so
 * rather than looking like its answers failed to load.
 *
 * Results merge every revision of a poll, so `questionNames` narrows them to
 * the questions of the version the respondent answered. It is ignored when
 * empty, since a schema that failed to parse would otherwise hide everything.
 * `answers` marks the respondent's own picks.
 */
export function SurveyResults({
  results,
  answers,
  questionNames,
}: {
  results: SurveyResultsData;
  answers?: Record<string, unknown>;
  questionNames?: ReadonlySet<string>;
}) {
  const questions =
    questionNames && questionNames.size > 0
      ? results.questions.filter((question) => questionNames.has(question.name))
      : results.questions;

  return (
    <VStack className="w-full" space="xl">
      <VStack space="xs">
        <Heading size="h4-lg">Results</Heading>

        <Text size="sm" className="text-typography-600">
          {formatRespondentCount(results.respondentCount)}
        </Text>
      </VStack>

      {questions.map((question) => (
        <SurveyResultQuestionBlock
          key={question.name}
          question={question}
          answer={answers?.[question.name]}
        />
      ))}
    </VStack>
  );
}
